<script lang="ts" setup>
import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import Menu from 'primevue/menu';
import { useToast } from 'primevue/usetoast';
import { OrganizationInterface, OrganizationsInterface, OrganizationTableInterface } from '@/types';
import AddOrganizationModal from '@/components/AddOrganizationModal/AddOrganizationModal.vue';
import OrganizationConnections from './OrganizationConnections.vue';
import { useStudentOrganizationStore } from '@/store';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface OrganizationPropsInterface {
  tableData?: OrganizationInterface[];
  studentId?: number | undefined;
  loading?: boolean;
}

const props = defineProps<OrganizationPropsInterface>();
const emit = defineEmits<{ (e: EMIT.UPDATE): void }>();
const organizationStudentStore = useStudentOrganizationStore();
const organizations: Ref<OrganizationInterface[] | undefined> = toRef(props, 'tableData');
const organizationCount: Ref<number> = computed(() => (organizations.value ? organizations.value.length : 0));
const organizationModalOpen = ref(false);
const toast = useToast();
const organizationItems: Ref<OrganizationsInterface[]> = ref([]);

const createMenuModel = (item: OrganizationTableInterface) => {
  const last = item[item.length - 1];
  const id = last.id;
  const name = last.name;
  const menuItem = {
    label: 'Remove Organization',
    command: () => detachOrganization(id, name),
    visible: can([PERMISSIONS.STUDENT_DETACH_ORGANIZATIONS]),
  };
  return [menuItem].filter((item) => item.visible);
};

const attachOrganization: (idsitem: number[]) => Promise<void> = async (ids) => {
  await organizationStudentStore
    .attachOrganizations({
      organizations: ids,
      studentId: props.studentId as number,
    })
    .then(() => {
      emit(EMIT.UPDATE);
      toast.add({
        severity: 'success',
        summary: 'Added',
        detail: 'Organization has been added',
        life: 3000,
      });
    })
    .catch((response) => {
      let detail = 'Student already belongs to an organization';
      if (response.data.errors.external_id) {
        detail = response.data.errors.external_id[0];
      }
      toast.add({
        severity: 'error',
        summary: 'Student could not be added to an organization',
        detail: detail,
        life: 3000,
      });
    });
};

const detachOrganization: (id: number[], organizationName: string) => Promise<void> = async (ids, organizationName) => {
  await organizationStudentStore.detachOrganizations({ organizations: [ids], studentId: props.studentId as number });
  toast.add({
    severity: 'success',
    summary: 'Removed',
    detail: `Organization ${organizationName} has been removed from student`,
    life: 3000,
  });
  emit(EMIT.UPDATE);
};

const openAddOrganizations = async () => {
  if (can([PERMISSIONS.STUDENT_ORGANIZATIONS_AVAILABLE_TO_ADD]) && can([PERMISSIONS.STUDENT_ATTACH_ORGANIZATIONS])) {
    organizationItems.value = await organizationStudentStore.getAddOrganizationsModalData(props.studentId as number);
    organizationModalOpen.value = true;
  } else {
    toast.add({
      severity: 'warning',
      summary: 'Error',
      detail: 'You do not have permission to add organizations',
      life: 3000,
    });
  }
};

const onCancelAdd = () => (organizationModalOpen.value = false);

const onSaveAdd = (organizations: number[]) => {
  attachOrganization(organizations);
  organizationModalOpen.value = false;
};

const menu = ref();

const onToggle = (event: Event): void => menu.value.toggle(event);
</script>

<template>
  <Card>
    <template #header>
      <h2>Organizations</h2>
      <Chip>{{ organizationCount }}</Chip>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.STUDENT_ATTACH_ORGANIZATIONS])"
          type="text"
          size="small"
          text="Add Organization"
          icon-left="add"
          @click="openAddOrganizations"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="organizations"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="loading"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-student-organization-store-table"
        state-storage="session"
        :class="{ 'table-loading': loading }"
      >
        <template v-if="organizationCount">
          <Column body-class="w-full" header-class="w-full" field="name" header="Organization">
            <template #body="{ data }">
              <OrganizationConnections :organizations="data" />
            </template>
          </Column>
          <Column
            body-class="justify-content-center mw-10rem"
            header-class="justify-content-center mw-10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <Icon name="more_horiz" class="text-lg p-1 cursor-pointer" @click="onToggle" />
              <Menu ref="menu" :model="createMenuModel(data)" :popup="true" />
            </template>
          </Column>
        </template>
        <template #empty>Organizations not assigned</template>
      </DataTable>
      <AddOrganizationModal
        v-if="organizationModalOpen && can([PERMISSIONS.STUDENT_ORGANIZATIONS_AVAILABLE_TO_ADD])"
        title="Add Organization"
        :items="organizationItems"
        :searchable="true"
        :multi-selection="false"
        @save="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>
